import React from 'react';
import { Box, Tooltip, Typography, alpha } from '@mui/material';
import { ScrollArea } from '../layout/ScrollArea';
import { SortHeader, type SortDirection } from './SortHeader';
import { NameLink } from '../inputs/NameLink';

export interface HeatmapCell {
  key: string;
  /** What the cell says. A cell with no value shows `emptyText`. */
  value: number | null;
  /** The whole story for the hover, e.g. "Tuesday · 596 MQLs · 11 applications". */
  title?: React.ReactNode;
  /**
   * Too little behind it to be worth reading. Keeps its place and its hover,
   * loses its colour — the difference between "this is low" and "we cannot
   * tell".
   */
  muted?: boolean;
}

export interface HeatmapRow {
  key: string;
  label: React.ReactNode;
  /** Shown under the label, quieter — a channel, a total, a count. */
  meta?: React.ReactNode;
  cells: HeatmapCell[];
  /**
   * The row as a whole, before its cells — totals, a blended rate. Drawn
   * plainly rather than shaded: they are what the cells vary around, not more
   * of them. One entry per `summaryColumns`, in the same order.
   */
  summaries?: (HeatmapCell | null)[];
}

export interface HeatmapProps {
  columns: { key: string; label: string }[];
  /** Which column the rows are in the order of, and which way. */
  sort?: { key: string; direction: SortDirection } | null;
  /** Called with a column's key — or `summaryKey`, or `rowKey` — when its heading is clicked. */
  onSort?: (key: string) => void;
  rows: HeatmapRow[];
  formatValue: (value: number) => string;
  /** The top of the colour scale. Defaults to the largest value shown. */
  max?: number;
  /** Heading over the row labels. */
  rowLabel?: string;
  /**
   * Columns for the row as a whole, drawn between the label and the cells.
   * Each takes its own formatter, since a total and a rate do not read alike.
   */
  summaryColumns?: { key: string; label: string; format?: (value: number) => string }[];
  /** Sort key for the row-label heading, when it sorts. */
  rowKey?: string;
  /** Said when there are no rows to draw. */
  emptyText?: string;
  /** Shown in a cell with no value. */
  cellEmptyText?: string;
  /** Names the grid for screen readers. */
  ariaLabel: string;
  /**
   * The columns share the full width and the row labels take only what they
   * need — for a grid of few columns (the weekdays) that should fill its
   * panel. Off by default: each column keeps a fixed width and the labels
   * take what is left.
   */
  stretch?: boolean;
  /**
   * A row along the foot for the columns as a whole — totals. Drawn plainly
   * like `summaries`, not shaded: the caller works them out (a total that
   * counts each person once is not the cells added up). One entry per
   * `summaryColumns` and per column, in the same order.
   */
  totalRow?: { label: React.ReactNode; summaries?: (HeatmapCell | null)[]; cells: (HeatmapCell | null)[] };
  /**
   * Makes each row's label a link that opens that row's detail — a campaign
   * opening its hours in a window. Called with the row's key.
   */
  onRowOpen?: (key: string) => void;
  /** What opening a row shows, for screen readers: "by hour of day". */
  rowOpenLabel?: string;
}

const MIN_TINT = 0.06;
const MAX_TINT = 0.92;
/** Past this much colour the ink has to flip to stay readable. */
const INK_FLIP = 0.55;

/**
 * A grid of one measure across two dimensions, shaded light to dark.
 *
 * One hue at varying strength, never a rainbow: the eye reads "more" and
 * "less" from a single hue without a legend, where a spectrum has to be
 * decoded. The scale runs from zero to the largest value drawn, so the
 * darkest cell is always the top of what is actually there.
 *
 * Row labels stay put while the columns scroll, and every cell carries its
 * own readout — a shaded square on its own is an impression, and the numbers
 * behind it are what someone acts on.
 */
export function Heatmap({
  columns, rows, formatValue, max, rowLabel, summaryColumns = [], emptyText, cellEmptyText = '—', ariaLabel,
  sort, onSort, rowKey, stretch = false, totalRow, onRowOpen, rowOpenLabel,
}: HeatmapProps) {
  /** A heading that sorts where the caller wants one, plain text otherwise. */
  const heading = (label: string, key: string | undefined, numeric: boolean) =>
    onSort && key ? (
      <SortHeader
        label={label}
        numeric={numeric}
        direction={sort?.key === key ? sort.direction : null}
        onClick={() => onSort(key)}
      />
    ) : (
      label
    );
  const top = React.useMemo(() => {
    if (max !== undefined) return max;
    const shown = rows.flatMap((r) => r.cells.filter((c) => !c.muted && c.value !== null).map((c) => c.value as number));
    return shown.length ? Math.max(...shown) : 0;
  }, [rows, max]);

  if (!rows.length) {
    return (
      <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.secondary, py: 2 })}>
        {emptyText ?? 'Nothing to show.'}
      </Typography>
    );
  }

  const intensity = (v: number) => (top <= 0 ? 0 : MIN_TINT + (Math.min(v, top) / top) * (MAX_TINT - MIN_TINT));

  return (
    <ScrollArea ariaLabel={ariaLabel} insetStart={1}>
      <Box
        component="table"
        sx={{ borderCollapse: 'separate', borderSpacing: 0, width: '100%', tableLayout: 'auto' }}
      >
        <Box component="thead">
          <Box component="tr">
            <Box
              component="th"
              scope="col"
              sx={(t) => ({
                ...t.typography.caption,
                position: 'sticky',
                left: 0,
                zIndex: 2,
                bgcolor: t.tokens.surface.raised,
                color: t.tokens.text.secondary,
                textAlign: 'left',
                fontWeight: 600,
                px: 1.5,
                py: 1,
                width: stretch ? '1%' : '100%',
                whiteSpace: 'nowrap',
                borderBottom: `1px solid ${t.tokens.border.subtle}`,
              })}
            >
              {heading(rowLabel ?? '', rowKey, false)}
            </Box>
            {summaryColumns.map((c, i) => (
              <Box
                key={`sum-${c.key}`}
                component="th"
                scope="col"
                sx={(t) => ({
                  ...t.typography.caption,
                  color: t.tokens.text.secondary,
                  fontWeight: 600,
                  px: 1,
                  py: 1,
                  minWidth: 72,
                  textAlign: 'center',
                  whiteSpace: 'nowrap',
                  borderBottom: `1px solid ${t.tokens.border.subtle}`,
                  ...(i === summaryColumns.length - 1 && {
                    borderRight: `1px solid ${t.tokens.border.subtle}`,
                  }),
                })}
              >
                {heading(c.label, c.key, true)}
              </Box>
            ))}
            {columns.map((c) => (
              <Box
                key={c.key}
                component="th"
                scope="col"
                sx={(t) => ({
                  ...t.typography.caption,
                  color: t.tokens.text.secondary,
                  fontWeight: 600,
                  px: 1,
                  py: 1,
                  minWidth: 72,
                  width: stretch ? undefined : 72,
                  textAlign: 'center',
                  whiteSpace: 'nowrap',
                  borderBottom: `1px solid ${t.tokens.border.subtle}`,
                })}
              >
                {heading(c.label, c.key, true)}
              </Box>
            ))}
          </Box>
        </Box>

        <Box component="tbody">
          {rows.map((row) => (
            <Box component="tr" key={row.key}>
              <Box
                component="th"
                scope="row"
                sx={(t) => ({
                  position: 'sticky',
                  left: 0,
                  zIndex: 1,
                  bgcolor: t.tokens.surface.raised,
                  textAlign: 'left',
                  fontWeight: 400,
                  px: 1.5,
                  py: 0.75,
                  width: stretch ? '1%' : '100%',
                  maxWidth: 420,
                  borderBottom: `1px solid ${t.tokens.border.subtle}`,
                  '[data-scrolled-start="true"] &': { boxShadow: t.tokens.shadow.sm },
                })}
              >
                {/* A floor under the name. Long names may break anywhere, so
                    without one the browser squeezes this column to a letter a
                    line the moment the page narrows (a chat panel opening);
                    with it the day columns scroll under the name instead. */}
                <Typography variant="body2" sx={{ lineHeight: 1.3, wordBreak: 'break-word', minWidth: 220 }}>
                  {onRowOpen ? (
                    <NameLink
                      onClick={() => onRowOpen(row.key)}
                      aria-label={typeof row.label === 'string' && rowOpenLabel ? `${row.label}, ${rowOpenLabel}` : undefined}
                    >
                      {row.label}
                    </NameLink>
                  ) : (
                    row.label
                  )}
                </Typography>
                {row.meta && (
                  <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary, display: 'block' })}>
                    {row.meta}
                  </Typography>
                )}
              </Box>

              {summaryColumns.map((c, i) => {
                const summary = row.summaries?.[i] ?? null;
                const show = c.format ?? formatValue;
                return (
                  <Box
                    key={`sum-${c.key}`}
                    component="td"
                    title={typeof summary?.title === 'string' ? summary.title : undefined}
                    sx={(t) => ({
                      ...t.typography.caption,
                      fontVariantNumeric: 'tabular-nums',
                      fontWeight: i === summaryColumns.length - 1 ? 700 : 400,
                      textAlign: 'center',
                      px: 1,
                      py: 0.75,
                      color: summary?.value == null ? t.tokens.text.tertiary : t.tokens.text.primary,
                      borderBottom: `1px solid ${t.tokens.border.subtle}`,
                      ...(i === summaryColumns.length - 1 && {
                        borderRight: `1px solid ${t.tokens.border.subtle}`,
                      }),
                    })}
                  >
                    {summary?.value == null ? cellEmptyText : show(summary.value)}
                  </Box>
                );
              })}
              {row.cells.map((cell) => {
                const shade = cell.value !== null && !cell.muted ? intensity(cell.value) : 0;
                const body = (
                  <Box
                    component="td"
                    sx={(t) => ({
                      ...t.typography.caption,
                      fontVariantNumeric: 'tabular-nums',
                      textAlign: 'center',
                      px: 1,
                      py: 0.75,
                      borderBottom: `1px solid ${t.tokens.border.subtle}`,
                      bgcolor: cell.muted || cell.value === null
                        ? t.tokens.surface.sunken
                        : alpha(t.tokens.brand.primary, shade),
                      color: cell.muted || cell.value === null
                        ? t.tokens.text.tertiary
                        : shade >= INK_FLIP
                          ? t.tokens.brand.onPrimary
                          : t.tokens.text.primary,
                      cursor: 'default',
                    })}
                  >
                    {cell.value === null || cell.muted ? cellEmptyText : formatValue(cell.value)}
                  </Box>
                );
                return cell.title ? (
                  <Tooltip key={cell.key} title={cell.title} placement="top">
                    {body}
                  </Tooltip>
                ) : (
                  <React.Fragment key={cell.key}>{body}</React.Fragment>
                );
              })}
            </Box>
          ))}
        </Box>

        {totalRow && (
          <Box component="tfoot">
            <Box component="tr">
              <Box
                component="th"
                scope="row"
                sx={(t) => ({
                  position: 'sticky',
                  left: 0,
                  zIndex: 1,
                  bgcolor: t.tokens.surface.raised,
                  textAlign: 'left',
                  px: 1.5,
                  py: 0.75,
                  borderTop: `1px solid ${t.tokens.border.default}`,
                  '[data-scrolled-start="true"] &': { boxShadow: t.tokens.shadow.sm },
                })}
              >
                <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
                  {totalRow.label}
                </Typography>
              </Box>
              {[
                ...summaryColumns.map((c, i) => ({ key: `sum-${c.key}`, cell: totalRow.summaries?.[i] ?? null, show: c.format ?? formatValue, last: i === summaryColumns.length - 1 })),
                ...columns.map((c, i) => ({ key: c.key, cell: totalRow.cells[i] ?? null, show: formatValue, last: false })),
              ].map(({ key, cell, show, last }) => (
                <Box
                  key={key}
                  component="td"
                  title={typeof cell?.title === 'string' ? cell.title : undefined}
                  sx={(t) => ({
                    ...t.typography.caption,
                    fontVariantNumeric: 'tabular-nums',
                    fontWeight: 700,
                    textAlign: 'center',
                    px: 1,
                    py: 0.75,
                    color: cell?.value == null ? t.tokens.text.tertiary : t.tokens.text.primary,
                    borderTop: `1px solid ${t.tokens.border.default}`,
                    ...(last && { borderRight: `1px solid ${t.tokens.border.subtle}` }),
                  })}
                >
                  {cell?.value == null ? cellEmptyText : show(cell.value)}
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </Box>
    </ScrollArea>
  );
}
